
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, X } from "lucide-react";

interface StudentProgramsPageSelectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  totalPrograms: number;
  onDownload: (startPage: number, endPage: number) => void;
  studentName: string;
}

const PROGRAMS_PER_PAGE = 2;

const StudentProgramsPageSelectionDialog = ({ 
  isOpen, 
  onClose, 
  totalPrograms,
  onDownload,
  studentName
}: StudentProgramsPageSelectionDialogProps) => {
  const [startPage, setStartPage] = useState(1);
  const [endPage, setEndPage] = useState(1);
  
  const totalPages = Math.ceil(totalPrograms / PROGRAMS_PER_PAGE);

  const handleDownload = () => {
    const validStartPage = Math.max(1, Math.min(startPage, totalPages));
    const validEndPage = Math.max(validStartPage, Math.min(endPage, totalPages));
    
    console.log('Programs page selection:', { validStartPage, validEndPage, totalPages });
    onDownload(validStartPage, validEndPage);
    onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            Select Programs Pages - {studentName}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogTitle>
          <DialogDescription>
            Choose which program pages to download ({totalPrograms} programs, {totalPages} pages, {PROGRAMS_PER_PAGE} programs per page)
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="startPage">Start Page</Label>
              <Input
                id="startPage"
                type="number"
                min="1"
                max={totalPages}
                value={startPage}
                onChange={(e) => setStartPage(parseInt(e.target.value) || 1)}
              />
            </div>
            <div>
              <Label htmlFor="endPage">End Page</Label>
              <Input
                id="endPage"
                type="number"
                min="1"
                max={totalPages}
                value={endPage}
                onChange={(e) => setEndPage(parseInt(e.target.value) || 1)}
              />
            </div>
          </div>
          
          <div className="text-sm text-muted-foreground">
            Total Pages Available: {totalPages} | Programs per page: {PROGRAMS_PER_PAGE}
          </div>

          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleDownload} disabled={totalPrograms === 0}>
              <Download className="w-4 h-4 mr-2" />
              Continue
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StudentProgramsPageSelectionDialog;
